/**
 * CR114/CR201: Asset Preview Card
 * 
 * Grid view card for assets with preview and actions.
 * CR201: Uses SafeAssetImage instead of dangerouslySetInnerHTML.
 */

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  MoreHorizontal, 
  Pencil, 
  Copy, 
  Archive, 
  RotateCcw, 
  Trash2,
} from "lucide-react";
import type { AppAsset } from "@/lib/asset-studio/types";
import { ASSET_TYPE_LABELS } from "@/lib/asset-studio/types";
import { SafeAssetImage } from "@/components/ui/SafeAssetImage";

interface AssetPreviewCardProps {
  asset: AppAsset;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
}

export function AssetPreviewCard({
  asset,
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  onDelete,
}: AssetPreviewCardProps) {
  return (
    <Card 
      className={`group relative overflow-hidden transition-shadow hover:shadow-md ${
        asset.status === "archived" ? "opacity-60" : ""
      }`}
    >
      {/* Preview Area - CR201: Uses SafeAssetImage instead of dangerouslySetInnerHTML */}
      <div 
        className="aspect-square bg-muted/30 flex items-center justify-center p-4 cursor-pointer"
        onClick={onEdit}
        style={{ color: asset.default_tint || "currentColor" }}
      >
        <SafeAssetImage
          url={asset.url}
          svg={asset.svg}
          alt={asset.name}
          className="w-full h-full object-contain"
          fallback={
            <div className="text-muted-foreground text-sm">No preview</div>
          }
        />
      </div>

      {/* Actions Overlay */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon" className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {asset.status === "active" ? (
              <DropdownMenuItem onClick={onArchive}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={onRestore}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-destructive">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Status badge */}
      {asset.status === "archived" && (
        <Badge 
          variant="secondary" 
          className="absolute top-2 left-2 text-[10px]"
        >
          Archived
        </Badge>
      )}

      {/* Info */}
      <CardContent className="p-3 space-y-1">
        <div className="font-medium text-sm truncate">{asset.name}</div>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-[10px]">
            {ASSET_TYPE_LABELS[asset.type]}
          </Badge>
          {asset.is_tileable && (
            <Badge variant="secondary" className="text-[10px]">
              Tileable
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
